import { renderPrincipal } from "@/shared/components/ResourceView";
import { RobotoLinkHoverUnderline } from "@/shared/components/RobotoLinkHoverUnderline";
import {
  cellStyles,
  createColumnHelper,
  renderDashOr,
  renderDashOrValue,
  type ICellRendererParams,
} from "@/shared/foundation/primitives/Table";
import { TagGroup } from "@/shared/foundation/primitives/Tag";
import { Goto } from "@/shared/navigation/Goto";
import { formatNanoDuration, nanoSecToDate } from "@/shared/time";

import { EventRecord } from "./EventRecord";

const columnHelper = createColumnHelper<EventRecord>();

/**
 * Normalize a nanosecond event time into a `Date` for `TimestampCell`, or
 * `null` (→ em dash) when absent. The column owns this conversion because it
 * knows the field is nanosecond `bigint`; the cell only speaks instants.
 */
function nanosToDateOrNull(
  ns: bigint | number | undefined | null,
): Date | null {
  if (ns === undefined || ns === null) {
    return null;
  }
  return nanoSecToDate(BigInt(ns));
}

export const eventsTableColumns = [
  columnHelper.accessor("event_id", {
    headerName: "ID",
    minWidth: 180,
    cellRenderer: (p: ICellRendererParams<EventRecord, string>) => {
      const record = p.data;
      if (!record) {
        return null;
      }
      return (
        <RobotoLinkHoverUnderline
          to={Goto.createEventVisualizerRoute(
            record.event_id,
            record.start_time || undefined,
          )}
          className={cellStyles.idCell}
        >
          {record.event_id}
        </RobotoLinkHoverUnderline>
      );
    },
  }),
  columnHelper.accessor("name", {
    headerName: "Name",
    flex: 3,
    minWidth: 200,
    sortable: true,
    cellClass: cellStyles.primaryContent,
    cellRenderer: renderDashOrValue,
  }),
  columnHelper.accessor("description", {
    headerName: "Description",
    flex: 1,
    cellRenderer: renderDashOrValue,
  }),
  columnHelper.timestamp("start_time", {
    headerName: "Start",
    minWidth: 120,
    sortable: true,
    variant: "precise",
    valueGetter: (p) => nanosToDateOrNull(p.data?.start_time),
  }),
  columnHelper.timestamp("end_time", {
    headerName: "End",
    minWidth: 120,
    sortable: true,
    variant: "precise",
    valueGetter: (p) => nanosToDateOrNull(p.data?.end_time),
  }),
  columnHelper.accessor((row) => row.end_time - row.start_time, {
    colId: "duration",
    headerName: "Duration",
    minWidth: 120,
    sortable: true,
    cellClass: cellStyles.monoCell,
    valueFormatter: (params) => formatNanoDuration(params.value),
  }),
  columnHelper.timestamp("created", {
    headerName: "Created",
    minWidth: 150,
    sortable: true,
    variant: "relative",
  }),
  columnHelper.accessor("created_by", {
    headerName: "Created by",
    minWidth: 150,
    sortable: true,
    cellRenderer: renderPrincipal,
  }),
  columnHelper.timestamp("modified", {
    headerName: "Modified",
    minWidth: 150,
    sortable: true,
    variant: "relative",
  }),
  columnHelper.accessor("modified_by", {
    headerName: "Modified by",
    minWidth: 150,
    sortable: true,
    cellRenderer: renderPrincipal,
  }),
  columnHelper.accessor("tags", {
    headerName: "Tags",
    flex: 3,
    minWidth: 180,
    cellClass: cellStyles.tagsCell,
    // Object types require a string formatting for accessibility
    valueFormatter: (p) => (p.value ?? []).join(", "),
    cellRenderer: renderDashOr(
      (p: ICellRendererParams<EventRecord, string[]>) => (
        <TagGroup tags={p.value ?? []} maxDisplayCount={3} />
      ),
    ),
  }),
];
